import React, { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { PauseIcon, PlayIcon, Volume2Icon, VolumeXIcon } from 'lucide-react';
import { productVideo } from '../../data/productVideo';
import { ShopNowButton } from '../shop/ShopNowButton';
import { track } from '../../utils/analytics';

/** Owner product video: autoplays muted while on screen, pauses when scrolled away. */
export function ProductVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const reduce = useReducedMotion();
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [userPaused, setUserPaused] = useState(false);
  const [failed, setFailed] = useState(false);
  const tracked = useRef(false);

  // Autoplay only while visible (and never when reduced motion is requested).
  useEffect(() => {
    const video = videoRef.current;
    if (!video || reduce) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !userPaused) {
        video.play().catch(() => setPlaying(false));
      } else if (!entry.isIntersecting) {
        video.pause();
      }
    }, {
      threshold: 0.4
    });
    observer.observe(video);
    return () => observer.disconnect();
  }, [reduce, userPaused]);
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      setUserPaused(false);
      video.play().catch(() => setPlaying(false));
    } else {
      setUserPaused(true);
      video.pause();
    }
  };
  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
    if (!video.muted && video.paused) video.play().catch(() => setPlaying(false));
  };
  const onPlay = () => {
    setPlaying(true);
    if (!tracked.current) {
      tracked.current = true;
      track('product_video_play', {
        video: productVideo.title
      });
    }
  };
  if (failed) return null;
  return <section aria-labelledby="video-title" className="border-t mf-hairline bg-[#050E1A]" data-id="element-349" data-mp="2s9fzlw0tpzs7">
      <div className="mx-auto grid max-w-site items-center gap-10 px-5 py-10 md:px-8 md:py-14 lg:grid-cols-[1fr_1.1fr] lg:gap-16" data-id="element-350" data-mp="2nbmr2wc1stor">
        <div className="order-2 lg:order-1" data-id="element-351" data-mp="2x1zaumc1gitu">
          <h2 id="video-title" className="font-display text-5xl font-semibold uppercase leading-[0.92] md:text-6xl" data-id="element-352" data-mp="2x30nuwcm7fe7">
            Same water. <span className="text-ice" data-id="element-353" data-mp="29cn19lcsu8r0">A whole new night.</span>
          </h2>
          <p className="mt-5 max-w-md text-lg text-titanium" data-id="element-354" data-mp="24eljd3r40fh4">
            Real M1 PRO systems switching on in real pools and backyards. One tap and the water lights up — no wires, no electrician.
          </p>
          <p className="mt-6 font-display text-base font-semibold uppercase tracking-[0.14em] text-ice" data-id="element-355" data-mp="2e9wt17nnuwi0">
            Your pool could be next.
          </p>
          <ShopNowButton source="product_video" showPrice className="mt-8 w-full sm:w-auto" data-id="element-356" data-mp="3pr6a5njbhcg5" />
        </div>

        <div className="order-1 lg:order-2" data-id="element-357" data-mp="14i8rl671c9t">
          <div className="mf-hud relative mx-auto w-fit overflow-hidden border border-marine/30 bg-black" data-id="element-358" data-mp="16upxw44nilt">
            <video ref={videoRef} src={productVideo.src} aria-label={productVideo.description} muted loop playsInline preload="metadata" onPlay={onPlay} onPause={() => setPlaying(false)} onError={() => setFailed(true)} onClick={togglePlay} className="block max-h-[78vh] w-full max-w-full cursor-pointer object-contain lg:max-h-[720px]" data-id="element-359" data-mp="61i4b25f2wmo" />

            <div className="absolute bottom-3 right-3 flex gap-2" data-id="element-360" data-mp="f4ax59u8zgem">
              <button type="button" onClick={togglePlay} aria-label={playing ? 'Pause video' : 'Play video'} className="grid h-11 w-11 place-items-center rounded-full border border-white/25 bg-deep/80 text-white backdrop-blur transition-colors duration-150 hover:bg-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-ice" data-id="element-361" data-mp="f2pzhbcmzhcj">
                {playing ? <PauseIcon className="h-4 w-4" aria-hidden data-id="element-362" data-mp="363gv5ot70muo" /> : <PlayIcon className="h-4 w-4" aria-hidden data-id="element-363" data-mp="3648sp56counl" />}
              </button>
              <button type="button" onClick={toggleMute} aria-label={muted ? 'Unmute video' : 'Mute video'} className="grid h-11 w-11 place-items-center rounded-full border border-white/25 bg-deep/80 text-white backdrop-blur transition-colors duration-150 hover:bg-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-ice" data-id="element-364" data-mp="3236pkd9bbygc">
                {muted ? <VolumeXIcon className="h-4 w-4" aria-hidden data-id="element-365" data-mp="3kywx0g00ln1m" /> : <Volume2Icon className="h-4 w-4" aria-hidden data-id="element-366" data-mp="ldrj1jjvjf5o" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}